import { useEffect, useState } from "react";
import { api, leerSesion, incluye, ErrorApi } from "../api.ts";
import { tamanoLegible, aLocal } from "../../../dominio/src/index.ts";
import { AvisoError, CampoClave, Seccion, useAccion } from "./comun.tsx";
import { CampoNumero } from "../componentes/CampoNumero.tsx";
import { ExportarHistorias } from "./ExportarHistorias.tsx";

interface Copia { id: string; archivo: string; origen: string; estado: string; inicio: string; fin: string | null; bytes: number | null; verificado: boolean; local: boolean; drive_estado: string; drive_error: string | null; error: string | null; filas: Record<string, number> | null }
interface Estado {
  activo: boolean; hora: string; retencion: { diarios: number; semanales: number; mensuales: number }; tieneClave: boolean; pista: string;
  alerta: string | null; proxima: string | null; ocupada: boolean;
  google: { disponible: boolean; conectado: boolean; email: string; desde: string | null; revocado: boolean };
  copias: Copia[];
}

const cuando = (iso: string | null) => {
  if (!iso) return "—";
  const l = aLocal(new Date(iso));
  const [a, m, d] = l.fecha.split("-");
  return `${d}/${m}/${a} ${l.hora}`;
};
const pl = (n: number | unknown, uno: string, varios: string) => `${n} ${Number(n) === 1 ? uno : varios}`;
const ORIGEN: Record<string, string> = { programado: "Automática", manual: "Manual", antes_de_restaurar: "Antes de restaurar" };

/** Copias de seguridad de la propia clínica: solo sus datos, con su clave y su Google Drive. */
export function CopiasClinica() {
  const [e, setE] = useState<Estado | null>(null);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error" | "info"; texto: string } | null>(null);
  const [accion, setAccion] = useState<{ tipo: "probar" | "restaurar"; id: string; archivo: string } | null>(null);
  const a = useAccion();

  const cargar = async () => { const r = await a.correr(() => api<Estado>("GET", "/api/mis-copias")); if (r) setE(r); return r; };
  useEffect(() => {
    cargar();
    const g = new URLSearchParams(location.hash.split("?")[1] ?? "").get("google");
    if (g) {
      setMsg(g === "ok" ? { tipo: "ok", texto: "Tu Google Drive quedó conectado. Cada copia se subirá a la carpeta «Donna · Copias de seguridad» de tu Drive." }
        : g === "cancelado" ? { tipo: "info", texto: "Cancelaste la conexión con Google Drive." }
        : { tipo: "error", texto: "No se pudo conectar Google Drive. Inténtalo de nuevo." });
      history.replaceState(null, "", "#/mis-copias");
    }
  }, []);
  // mientras hay una copia en curso se actualiza cada 2 segundos
  useEffect(() => {
    if (!e?.ocupada && !e?.copias.some((x) => x.estado === "en_curso")) return;
    const t = setTimeout(cargar, 2000);
    return () => clearTimeout(t);
  }, [e]);

  if (!e) return <Seccion titulo="Copias de mi clínica"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;

  async function copiarAhora() {
    setMsg(null);
    if (await a.correr(() => api("POST", "/api/mis-copias"))) { setMsg({ tipo: "info", texto: "Copia en curso. Puedes seguir trabajando." }); await cargar(); }
  }
  async function descargar(x: Copia) {
    const s = leerSesion();
    await a.correr(async () => {
      const r = await fetch(`/api/mis-copias/${x.id}/descargar`, { headers: s ? { authorization: `Bearer ${s.token}` } : {} });
      if (!r.ok) throw new ErrorApi(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? "No se pudo descargar.");
      const url = URL.createObjectURL(await r.blob());
      const enlace = document.createElement("a"); enlace.href = url; enlace.download = x.archivo; enlace.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    });
  }
  async function borrar(x: Copia) {
    if (!confirm(`¿Borrar la copia del ${cuando(x.inicio)}?${x.drive_estado === "subido" ? " También se borra de tu Google Drive." : ""}`)) return;
    const r = await a.correr(() => api<Estado>("DELETE", `/api/mis-copias/${x.id}`));
    if (r) setE(r);
  }
  async function subir(x: Copia) {
    const r = await a.correr(() => api<Estado>("POST", `/api/mis-copias/${x.id}/drive`));
    if (r) setE(r);
  }
  const ultimo = e.copias.find((x) => x.estado === "ok");

  return (
    <div className="respaldos">
      {msg && <div className={`aviso ${msg.tipo}`} role="status">{msg.texto}</div>}
      {e.alerta && <div className="aviso error" role="alert">{e.alerta}</div>}
      <AvisoError error={a.error} />

      <Seccion titulo="Copias de mi clínica" acciones={<button className="btn pri" onClick={copiarAhora} disabled={a.ocupado || e.ocupada || !e.tieneClave}>{e.ocupada ? "Copia en curso…" : "Hacer una copia ahora"}</button>}>
        <div className="resumen-respaldo">
          <div><span className="muted">Última copia correcta</span><b>{cuando(ultimo?.fin ?? null)}</b>{ultimo && <small className="muted">{tamanoLegible(ultimo.bytes ?? 0)} · {ultimo.verificado ? "verificada" : "sin verificar"}</small>}</div>
          <div><span className="muted">Próxima automática</span><b>{e.activo && e.proxima ? (new Date(e.proxima).getTime() - Date.now() < 60000 ? "En los próximos minutos" : cuando(e.proxima)) : "Desactivada"}</b><small className="muted">todos los días a las {e.hora}</small></div>
          <div><span className="muted">En tu Google Drive</span><b>{e.google.conectado ? e.google.email || "Conectado" : "No conectado"}</b>{e.google.conectado && <small className="muted">carpeta «Donna · Copias de seguridad»</small>}</div>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Cada copia guarda <b>solo los datos de tu clínica</b> (pacientes, historias, odontogramas, fotos, citas, pagos, documentos firmados y registro de actividad),
          va cifrada con la clave que tú eliges y se verifica al crearla. Es independiente de las copias que Donna hace del servidor completo.
        </p>
      </Seccion>

      <ExportarHistorias />

      <div className="dos">
        <Clave e={e} alGuardar={(r) => { setE({ ...e, ...r }); setMsg({ tipo: "ok", texto: "Clave guardada. Anótala en un lugar seguro, fuera del consultorio." }); }} />
        {incluye("respaldo_nube")
          ? <Drive e={e} alCambiar={(r) => setE({ ...e, ...r })} />
          : <p className="muted" style={{ margin: 0 }}>Guardar tus copias en tu propio Google Drive es una función de los planes Clínica y Total (Configuración → Plan y suscripción).</p>}
      </div>

      <Programacion e={e} alGuardar={(r) => { setE({ ...e, ...r }); setMsg({ tipo: "ok", texto: "Programación guardada." }); }} />

      <Seccion titulo="Copias guardadas">
        {!e.copias.length ? <p className="muted">Todavía no hay copias.</p> : (
          <div className="tabla-scroll">
            <table className="tabla-resp" aria-label="Copias guardadas">
              <thead><tr><th>Fecha</th><th>Tipo</th><th>Tamaño</th><th>Estado</th><th>Dónde está</th><th></th></tr></thead>
              <tbody>
                {e.copias.map((x) => (
                  <tr key={x.id}>
                    <td className="mono">{cuando(x.inicio)}</td>
                    <td>{ORIGEN[x.origen] ?? x.origen}</td>
                    <td>{x.bytes ? tamanoLegible(x.bytes) : "—"}</td>
                    <td>
                      {x.estado === "ok" ? <span className="chip e-confirmada">{x.verificado ? "Verificada" : "Correcta"}</span>
                        : x.estado === "en_curso" ? <span className="chip">En curso…</span>
                        : <span className="chip rojo" title={x.error ?? ""}>Falló</span>}
                      {x.estado === "error" && x.error && <div className="muted" style={{ fontSize: 12 }}>{x.error}</div>}
                    </td>
                    <td>
                      {x.local && <span className="chip">Servidor</span>}{" "}
                      {x.drive_estado === "subido" && <span className="chip azul">Drive</span>}
                      {x.drive_estado === "error" && <span className="chip rojo" title={x.drive_error ?? ""}>Drive: falló</span>}
                      {x.drive_estado === "pendiente" && <span className="chip">Drive: pendiente</span>}
                    </td>
                    <td className="acciones-fila">
                      {x.estado === "ok" && <>
                        {x.local && <button className="btn chico" onClick={() => descargar(x)}>Descargar</button>}
                        <button className="btn chico" onClick={() => setAccion({ tipo: "probar", id: x.id, archivo: x.archivo })}>Probar</button>
                        <button className="btn chico peligro" onClick={() => setAccion({ tipo: "restaurar", id: x.id, archivo: x.archivo })}>Restaurar</button>
                        {e.google.conectado && x.local && (x.drive_estado === "error" || x.drive_estado === "pendiente") && <button className="btn chico" onClick={() => subir(x)}>Subir a Drive</button>}
                      </>}
                      {x.estado !== "en_curso" && <button className="btn chico" aria-label="Borrar copia" onClick={() => borrar(x)}>Borrar</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {accion && <Accion accion={accion} alCerrar={() => setAccion(null)} alTerminar={(texto) => { setAccion(null); setMsg({ tipo: "ok", texto }); cargar(); }} />}
      </Seccion>
    </div>
  );
}

function Clave({ e, alGuardar }: { e: Estado; alGuardar: (r: Estado) => void }) {
  const [abierto, setAbierto] = useState(!e.tieneClave);
  const [clave, setClave] = useState(""), [conf, setConf] = useState(""), [pista, setPista] = useState(e.pista);
  const [anotada, setAnotada] = useState(false);
  const a = useAccion();
  async function guardar() {
    const r = await a.correr(() => api<Estado>("PUT", "/api/mis-copias/clave", { clave, confirmacion: conf, pista }));
    if (r) { alGuardar(r); setAbierto(false); setClave(""); setConf(""); setAnotada(false); }
  }
  return (
    <Seccion titulo="Clave de las copias">
      {e.tieneClave && !abierto ? (
        <>
          <p style={{ margin: 0 }}>✔ Definida.{e.pista && <span className="muted"> Pista: {e.pista}</span>}</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Se necesita para abrir una copia fuera de Donna. Donna la guarda cifrada para hacer las copias automáticas.</p>
          <div><button className="btn chico" onClick={() => setAbierto(true)}>Cambiar clave</button></div>
        </>
      ) : (
        <>
          <div className="aviso info" style={{ fontSize: 13 }}>
            <b>Importante:</b> las copias se cifran con esta clave. <b>Sin ella no se pueden abrir</b>, ni siquiera nosotros podemos recuperarla.
            Anótala en papel y guárdala fuera del consultorio.
            {e.tieneClave && " Las copias anteriores seguirán necesitando la clave anterior."}
          </div>
          <CampoClave etiqueta="Clave (12 caracteres o más)" autoComplete="new-password" value={clave} onChange={setClave} />
          <CampoClave etiqueta="Repite la clave" autoComplete="new-password" value={conf} onChange={setConf} />
          <label className="campo">Pista (opcional, sin escribir la clave)<input className="entrada" value={pista} maxLength={80} onChange={(x) => setPista(x.target.value)} /></label>
          <label className="fila" style={{ gap: 8, fontSize: 14 }}><input type="checkbox" checked={anotada} onChange={() => setAnotada(!anotada)} /> Ya anoté la clave en un lugar seguro</label>
          <AvisoError error={a.error} />
          <div className="fila">
            <button className="btn pri" onClick={guardar} disabled={a.ocupado || !anotada || !clave}>Guardar clave</button>
            {e.tieneClave && <button className="btn" onClick={() => setAbierto(false)}>Cancelar</button>}
          </div>
        </>
      )}
    </Seccion>
  );
}

function Drive({ e, alCambiar }: { e: Estado; alCambiar: (r: Estado) => void }) {
  const a = useAccion();
  async function conectar() {
    const r = await a.correr(() => api<{ url: string }>("POST", "/api/mis-copias/google/conectar"));
    if (r) location.href = r.url;
  }
  async function desconectar() {
    if (!confirm("¿Desconectar tu Google Drive? Las copias que ya están en tu Drive se quedan ahí; las nuevas solo se guardarán en el servidor.")) return;
    const r = await a.correr(() => api<Estado>("POST", "/api/mis-copias/google/desconectar"));
    if (r) alCambiar(r);
  }
  return (
    <Seccion titulo="Tu Google Drive">
      {!e.google.disponible ? (
        <p style={{ margin: 0 }}>Google Drive todavía no está habilitado en este servidor. Pide a quien administra Donna que lo active; mientras tanto, puedes descargar tus copias y guardarlas tú.</p>
      ) : e.google.conectado ? (
        <>
          <p style={{ margin: 0 }}>✔ Conectado con <b>{e.google.email || "tu cuenta de Google"}</b>{e.google.desde && <span className="muted"> desde el {cuando(e.google.desde).slice(0, 10)}</span>}.</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Cada copia se sube a la carpeta «Donna · Copias de seguridad» de tu Drive. Donna solo tiene permiso sobre los archivos que ella crea: no puede ver el resto de tu Drive.</p>
          <div><button className="btn chico" onClick={desconectar} disabled={a.ocupado}>Desconectar</button></div>
        </>
      ) : (
        <>
          {e.google.revocado && <div className="aviso error" style={{ fontSize: 13 }}>Google retiró el permiso de Donna (o venció). Vuelve a conectar tu Drive para seguir subiendo las copias.</div>}
          <p style={{ margin: 0 }}>Guarda una copia fuera del consultorio y del servidor: si algo se daña o se pierde, tus datos siguen a salvo, en <b>tu</b> cuenta de Google.</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Donna solo pedirá permiso para manejar los archivos que ella crea en tu Drive. Las copias van cifradas con tu clave: Google no puede leerlas.</p>
          <div><button className="btn pri" onClick={conectar} disabled={a.ocupado}>Conectar mi Google Drive</button></div>
        </>
      )}
      <AvisoError error={a.error} />
    </Seccion>
  );
}

function Programacion({ e, alGuardar }: { e: Estado; alGuardar: (r: Estado) => void }) {
  const [activo, setActivo] = useState(e.activo), [hora, setHora] = useState(e.hora), [ret, setRet] = useState(e.retencion);
  const a = useAccion();
  const n = (k: keyof Estado["retencion"]) => (
    <CampoNumero className="entrada" min={k === "diarios" ? 1 : 0} max={60} valor={ret[k]} alCambiar={(n) => setRet({ ...ret, [k]: n })} />
  );
  async function guardar() {
    const r = await a.correr(() => api<Estado>("PUT", "/api/mis-copias/config", { activo, hora, retencion: ret }));
    if (r) alGuardar(r);
  }
  return (
    <Seccion titulo="Programación y copias que se conservan" acciones={<button className="btn pri" onClick={guardar} disabled={a.ocupado}>Guardar</button>}>
      <div className="rejilla">
        <label className="fila" style={{ gap: 8 }}><input type="checkbox" checked={activo} onChange={() => setActivo(!activo)} /> Copia automática diaria</label>
        <label className="campo">Hora<input className="entrada" type="time" value={hora} onChange={(x) => setHora(x.target.value)} /></label>
        <label className="campo">Copias diarias{n("diarios")}</label>
        <label className="campo">Copias semanales{n("semanales")}</label>
        <label className="campo">Copias mensuales{n("mensuales")}</label>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Con {ret.diarios} diarias, {ret.semanales} semanales y {ret.mensuales} mensuales puedes volver a cualquiera de los últimos {ret.diarios} días,
        y a un punto de cada semana y cada mes hasta {ret.mensuales} meses atrás. Las copias más viejas se borran solas (también en tu Drive).
      </p>
      <AvisoError error={a.error} />
    </Seccion>
  );
}

function Accion({ accion, alCerrar, alTerminar }: { accion: { tipo: "probar" | "restaurar"; id: string; archivo: string }; alCerrar: () => void; alTerminar: (texto: string) => void }) {
  const [clave, setClave] = useState(""), [conf, setConf] = useState("");
  const a = useAccion();
  const restaurar = accion.tipo === "restaurar";
  async function ejecutar() {
    const r = await a.correr(() => api<{ pacientes: number; citas: number; fotos: number; creado: string }>("POST", `/api/mis-copias/${accion.id}/${accion.tipo}`, { clave, confirmacion: conf }));
    if (!r) return;
    if (restaurar) {
      alTerminar(`Datos restaurados al ${cuando(r.creado)}: ${pl(r.pacientes, "paciente", "pacientes")}, ${pl(r.citas, "cita", "citas")} y ${pl(r.fotos, "foto", "fotos")}. Antes se guardó una copia del estado anterior. La página se recargará.`);
      setTimeout(() => location.reload(), 4000);
    } else alTerminar(`Prueba correcta: la copia del ${cuando(r.creado)} se abre con la clave y está completa (${pl(r.pacientes, "paciente", "pacientes")}, ${pl(r.citas, "cita", "citas")}, ${pl(r.fotos, "foto", "fotos")}). Sirve.`);
  }
  return (
    <div className={`panel-accion${restaurar ? " peligro" : ""}`} role="dialog" aria-label={restaurar ? "Restaurar copia" : "Probar copia"}>
      <h3 style={{ margin: 0 }}>{restaurar ? "Restaurar esta copia" : "Probar esta copia"}</h3>
      <p className="mono muted" style={{ margin: 0, fontSize: 12 }}>{accion.archivo}</p>
      {restaurar ? (
        <div className="aviso error" style={{ fontSize: 14 }}>
          Los datos de tu clínica volverán a como estaban en ese momento: <b>lo registrado después se perderá</b> (el registro de actividad no se toca).
          Antes de restaurar, Donna guardará una copia de lo que hay ahora. Solo cambia tu clínica.
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 14 }}>Donna abre la copia con la clave y comprueba que está completa y sin daños. Tus datos actuales no se tocan.</p>
      )}
      <CampoClave etiqueta={<>Clave de las copias <span className="muted" style={{ fontWeight: 400 }}>(déjala vacía para usar la clave guardada)</span></>} autoComplete="off" value={clave} onChange={setClave} />
      {restaurar && <label className="campo">Escribe RESTAURAR para confirmar<input className="entrada" value={conf} onChange={(x) => setConf(x.target.value)} /></label>}
      <AvisoError error={a.error} />
      <div className="fila">
        <button className={`btn ${restaurar ? "peligro" : "pri"}`} onClick={ejecutar} disabled={a.ocupado || (restaurar && conf !== "RESTAURAR")}>
          {a.ocupado ? (restaurar ? "Restaurando…" : "Probando…") : restaurar ? "Restaurar" : "Probar la copia"}
        </button>
        <button className="btn" onClick={alCerrar} disabled={a.ocupado}>Cancelar</button>
      </div>
    </div>
  );
}
